import { useEffect, useMemo, useRef } from "react";
import { buildAudioUrl, cacheManager } from "@/service/cache";
import {
  usePlayerCurrentList,
  usePlayerCurrentSongIndex,
  usePlayerLoop,
  usePlayerMediaType,
  usePlayerShuffle,
} from "@/store/player.store";
import { LoopState } from "@/types/playerContext";

export function usePreloadAudio() {
  const preloadRef = useRef<HTMLAudioElement | null>(null);
  const preloadedSongIdRef = useRef<string | null>(null);
  const preloadBlobUrlRef = useRef<string | null>(null);
  const { isSong } = usePlayerMediaType();
  const isShuffleActive = usePlayerShuffle();
  const loopState = usePlayerLoop();
  const currentList = usePlayerCurrentList();
  const currentSongIndex = usePlayerCurrentSongIndex();

  const nextSongId = useMemo(() => {
    if (!isSong) return null;
    if (isShuffleActive) return null;
    if (loopState === LoopState.One) return null;
    const nextIndex = currentSongIndex + 1;
    if (nextIndex < currentList.length) {
      return currentList[nextIndex].id;
    }
    if (loopState === LoopState.All && currentList.length > 0) {
      return currentList[0].id;
    }
    return null;
  }, [isSong, isShuffleActive, loopState, currentSongIndex, currentList]);

  useEffect(() => {
    if (!nextSongId) {
      if (preloadRef.current) {
        preloadRef.current.removeAttribute("src");
        preloadRef.current.load();
        preloadRef.current = null;
        preloadedSongIdRef.current = null;
      }
      if (preloadBlobUrlRef.current) {
        URL.revokeObjectURL(preloadBlobUrlRef.current);
        preloadBlobUrlRef.current = null;
      }
      return;
    }

    if (preloadedSongIdRef.current === nextSongId) return;

    if (preloadBlobUrlRef.current) {
      URL.revokeObjectURL(preloadBlobUrlRef.current);
      preloadBlobUrlRef.current = null;
    }

    if (preloadRef.current) {
      preloadRef.current.removeAttribute("src");
      preloadRef.current.load();
    }

    if (!preloadRef.current) {
      preloadRef.current = new Audio();
      preloadRef.current.preload = "auto";
    }

    let cancelled = false;

    (async () => {
      const cachedUrl = await cacheManager.getCachedAudioUrl(nextSongId);
      if (cancelled) {
        if (cachedUrl) URL.revokeObjectURL(cachedUrl);
        return;
      }

      if (cachedUrl) {
        if (preloadRef.current) {
          preloadRef.current.src = cachedUrl;
        }
        preloadBlobUrlRef.current = cachedUrl;
      } else {
        if (preloadRef.current) {
          preloadRef.current.src = buildAudioUrl(nextSongId, "stream");
        }
      }
      preloadedSongIdRef.current = nextSongId;
    })();

    return () => {
      cancelled = true;
    };
  }, [nextSongId]);

  useEffect(() => {
    return () => {
      if (preloadRef.current) {
        preloadRef.current.removeAttribute("src");
        preloadRef.current.load();
        preloadRef.current = null;
      }
      if (preloadBlobUrlRef.current) {
        URL.revokeObjectURL(preloadBlobUrlRef.current);
        preloadBlobUrlRef.current = null;
      }
    };
  }, []);
}
